/**
 * AnnotationSuggestionPanel — displayed in Scoring Studio when a teaching
 * video has been linked to the template.
 *
 * Pulls suggestion data from useTeachingVideoResult() and presents:
 *   - Suggested checks: Accept (one) or "Add all suggested checks"
 *   - Areas to watch (body-part emphasis): same two actions
 *   - Coaching cues / common mistakes: click-to-copy into the template
 *   - Phases the instruction described: NAMES and hints only (owner D8)
 *
 * ─── Accept now calls the server (PR-3) ─────────────────────────────────────
 *
 * Accept used to hand the raw suggestion to the parent, which ran a 632-line
 * client-side converter (`utils/suggestedGoalToAnnotation.ts`) to build the
 * goal config itself. The backend had a second converter for the same data and
 * the two drifted: the client wrote `target_min_degrees` /
 * `target_max_degrees` / `verbal_target_priority`, which `JointAngleConfig`
 * does not have, so the angle range a coach said out loud was dropped by
 * Pydantic on every write (TV-3 / FE-1 / E2E-3).
 *
 * Accept now posts the selected items to
 * `POST /teaching-videos/{id}/materialize` and appends exactly what comes back
 * (`onAnnotationsMaterialized`). Two consequences the coach can see:
 *   · Nothing is appended silently and nothing is dropped silently. An item is
 *     either in `annotations[]` or in `dropped[]` with a reason, and the reason
 *     renders — on the card and in `MaterializationNotices` (E2E-5 / E2E-6:
 *     three of one coach's twelve checkpoints were accepted broken, died on
 *     every evaluation, and the report blamed a temporary measurement error).
 *   · Every coach-facing sentence about a run comes from the backend notice
 *     catalogue and is rendered VERBATIM. This panel writes no explanation of
 *     its own.
 *
 * The old copy claimed "Accept opens the goal config form pre-filled". No form
 * was ever opened — the annotation was appended and the coach had to find it
 * (FE-3). The copy now says where the check went.
 *
 * ─── What you said beats what your body did (PR-7) ──────────────────────────
 *
 * A check built from a number the coach stated carries BOTH numbers: the one
 * they said, and the one their own demo measured (`verbal_target`). When the
 * two disagree the card says which one is in force, shows the backend's
 * explanation verbatim, and offers one switch to the other — see
 * `VerbalTargetRow` and `utils/verbalTargetCopy.ts`. Switching re-materialises
 * that one check and REPLACES it in the parent's list, so the coach's other
 * edits stay exactly as they are.
 */

import { useCallback, useState } from 'react';
import {
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { useTeachingVideoResult } from '@/hooks/useTeachingVideo';
import Badge from '@/components/ui/Badge';
import { Alert, Spinner } from '@/components/ui';
import MaterializationNotices, { NoticeRow } from './MaterializationNotices';
import { MATERIALIZE_FAILURE, materializeSuggestions } from '@/services/teachingVideoApi';
import {
  buildVerbalTargetPriority,
  emphasisSourceRef,
  goalSourceRef,
  mapAnnotationsToRequestedRefs,
} from '@/utils/materialization';
import {
  MATCHES_YOUR_DEMO_SENTENCE,
  SWITCH_PENDING_LABEL,
  VERBAL_TARGET_CONFLICT_KIND,
  hasVerbalTargetConflict,
  judgedAgainstLine,
  nextVerbalTargetPriority,
  offersVerbalTargetSwitch,
  showsMatchesDemo,
  switchActionLabel,
} from '@/utils/verbalTargetCopy';
import type { SuggestedGoal, CoachingPhase, CommonMistake, BodyPartEmphasis } from '@/types/teachingVideo';
import type {
  GoalApplicability,
  MaterializedAnnotation,
  Notice,
  Phase,
  VerbalTarget,
} from '@/types/api';
import type { ValidatedSuggestion } from '@/types/ruleCompiler';
import {
  applicabilityLabel,
  applicabilityVariant,
  humanizeRelation,
  buildValidationEvidence,
  providerLabel,
} from '@/utils/ruleCompilerDisplay';
import { landmarkDisplayName, landmarkDisplayNames } from '@/utils/landmarkDisplay';
import { gateSummarySentence, readGateSummary } from '@/utils/gateCopy';
import { normalizePhaseName } from '@/utils/phaseNameMatch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AnnotationSuggestionPanelProps {
  /**
   * Teaching video ID to poll for suggestions. Omit (and pass
   * `compiledRuleSuggestions` instead) to use rule-compiler mode.
   */
  teachingVideoId?: string;
  /**
   * The coach's own phases. Sent with every materialise call so the SERVER
   * reconciles each item's phase NAME — that decision used to be made twice,
   * once here and once on the backend.
   */
  templatePhases?: Phase[];
  /**
   * Called with the ready-to-save annotations the server built. The parent
   * appends them to the template's annotation list as they are: they carry no
   * canvas coordinates (owner decision D5) and need no client-side fixing up.
   *
   * `replacesIds` (PR-7) is passed ONLY when this run rebuilt a check the
   * parent already holds — the coach switched which number it is judged
   * against, so the server returned a new version of the same check. The
   * parent must then REPLACE those ids rather than append
   * (`replaceMaterializedAnnotations` in `utils/materialization.ts`), or the
   * coach ends up with two copies of one check, both counting. It is absent on
   * an ordinary first accept, so a parent written before PR-7 keeps working
   * unchanged.
   */
  onAnnotationsMaterialized?: (
    annotations: MaterializedAnnotation[],
    replacesIds?: string[],
  ) => void;
  /** Called when user clicks a coaching cue to add it to the template. */
  onAddCue?: (cue: string) => void;
  /** Called when user clicks a common mistake to add it to the template. */
  onAddMistake?: (mistake: string) => void;

  /**
   * Rule-compiler mode (contract docs/RULE_COMPILER_CONTRACT.md §6). Pass
   * suggestions directly instead of `teachingVideoId` — the panel skips its
   * internal teaching-video fetch entirely and renders these suggestion
   * cards. Mutually exclusive with `teachingVideoId`; when both are absent
   * the component falls through to the teaching-video branch with a
   * disabled (`enabled: false`) query, matching prior behavior for an
   * undefined id.
   */
  compiledRuleSuggestions?: ValidatedSuggestion[];
  /**
   * Called when the coach accepts a validated compiled-rule suggestion. May
   * be async (network call) — errors are caught and shown inline on the card.
   *
   * Return (or resolve to) `true` when the accept was DIVERTED — the
   * backend's rule-channel accept-time hook excluded the annotation as a
   * duplicate of one already on the template instead of appending it
   * (`Template.rule_channel_diverted`, docs/RUNBOOK_RULE_CHANNEL_ARMING.md).
   * The card uses this to show "Recorded" instead of "Added". Returning
   * `false`/`undefined`/`void` means the annotation was appended normally.
   */
  onAcceptCompiledRule?: (suggestion: ValidatedSuggestion) => boolean | void | Promise<boolean | void>;
  /**
   * Called when the coach dismisses a compiled-rule suggestion.
   *
   * PR-8: this now PERSISTS. Dismissal used to be local-only by contract §6
   * ("suggestions are replaced wholesale on the next compile, so there is
   * nothing to reconcile server-side") — but a reload resurrected every
   * dismissed suggestion, so a coach who had already decided against five of
   * them had to decide again every visit (frontend-audit vision gap 7). May
   * return a promise; a rejection is shown on the card.
   */
  onDismissCompiledRule?: (suggestionId: string) => void | Promise<void>;
  /**
   * PR-8 — batch selection, owned by the parent because the parent is what
   * posts the batch and shows its dry-run preview. Omit for a list with no
   * batch action, in which case no checkboxes render.
   */
  compiledRuleSelection?: CompiledRuleSelection;
  /**
   * The coach flipped a suggestion's "something to do / something to avoid"
   * badge.
   *
   * Reported UP because the correction has to travel with the BATCH: the
   * accept request carries `overrides` keyed by suggestion id, and the parent
   * is what posts it. The card keeps showing its own local value — this is the
   * parent learning about it, not taking it over.
   */
  onCompiledRuleSentimentOverride?: (
    suggestionId: string,
    value: 'positive' | 'negative',
  ) => void;
  /**
   * Disables Accept on every card while true — the parent sets this while
   * ANY accept is in flight (not just the clicked card's own pending state).
   * Accepting appends into the template's annotations array via a full PUT
   * replace; two accepts racing could otherwise silently drop one.
   */
  compiledRuleAcceptDisabled?: boolean;
}

/** What has happened to one suggestion card, as the coach sees it. */
type CardState = 'idle' | 'pending' | 'added' | 'not_added';

// ---------------------------------------------------------------------------
// Goal type display
// ---------------------------------------------------------------------------

// Coach words for what each check looks at — never the internal goal-type
// token (docs/COACH_FACING_COPY_STANDARD.md §2: "never: goal-type names").
// Keep these short; they render in a small badge.
const GOAL_TYPE_LABELS: Record<string, string> = {
  axis_goal: 'line check',
  timing_goal: 'timing check',
  joint_angle_goal: 'angle check',
  spatial_goal: 'position check',
  movement_goal: 'movement check',
  // Matches the wording the compiled-rule card uses for the same idea
  // ("Position past a line", ruleCompilerDisplay.RELATION_LABELS) — a coach
  // sees both in this panel, so they must not use two vocabularies. It also
  // keeps "distance" free for separation_ratio's "Distance apart".
  signed_projection_goal: 'past-a-line check',
  height_comparison_goal: 'height check',
};

/** Generic, coach-safe fallback for a goal type not in the table above —
 * NEVER the raw `goal_type` token (docs/COACH_FACING_COPY_STANDARD.md §2). */
const GENERIC_GOAL_TYPE_LABEL = 'check';

const IMPORTANCE_VARIANTS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};

// ---------------------------------------------------------------------------
// Bulk triage by importance (PR-8; frontend-audit vision gaps 5 and 13)
// ---------------------------------------------------------------------------

/**
 * Most important first. This is the order a coach reviews in, and it is also
 * the order that matters to the score: `importance` drives the 4x / 2.5x / 1x /
 * 0.5x weighting a checkpoint carries (CLAUDE.md, unified BDS/JAS).
 *
 * Sorting and filtering are on IMPORTANCE, deliberately not on the extractor's
 * `confidence` (vision gap 13): on the live 12-goal run every goal came back at
 * 0.8 including one that could not be measured at all, so confidence does not
 * discriminate and ordering by it would be noise dressed as triage.
 */
const IMPORTANCE_ORDER = ['critical', 'high', 'medium', 'low'] as const;
type ImportanceLevel = (typeof IMPORTANCE_ORDER)[number];

/** Capitalised for display; same words the badge already shows. */
const IMPORTANCE_FILTER_LABELS: Record<ImportanceLevel, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** Sort key: a level we do not recognise sorts last rather than throwing. */
function importanceRank(importance: string | undefined): number {
  const index = IMPORTANCE_ORDER.indexOf((importance ?? '') as ImportanceLevel);
  return index === -1 ? IMPORTANCE_ORDER.length : index;
}

/**
 * A compiled-rule suggestion's importance, read from the annotation the
 * backend built. `annotation` is deliberately untyped (contract §2 — appended
 * VERBATIM, never re-typed field by field), so this is the one place that looks
 * inside it for triage, and it fails closed: an unreadable value is `undefined`
 * and sorts last rather than being guessed at.
 */
function suggestionImportance(suggestion: ValidatedSuggestion): string | undefined {
  const value = suggestion.annotation?.importance;
  return typeof value === 'string' ? value : undefined;
}

/**
 * Shared sort + filter bar for a list of suggestions, with the select-all
 * control when the caller is collecting a batch.
 *
 * One bar for both lists on purpose: a coach triaging twelve suggestions needs
 * the same two actions whichever list they came from, and two near-identical
 * bars would drift.
 */
function SuggestionTriageBar({
  levelsPresent,
  importanceFilter,
  onImportanceFilterChange,
  mostImportantFirst,
  onMostImportantFirstChange,
  shownCount,
  totalCount,
  selection,
  idPrefix,
}: {
  levelsPresent: ImportanceLevel[];
  importanceFilter: ImportanceLevel | 'all';
  onImportanceFilterChange: (value: ImportanceLevel | 'all') => void;
  mostImportantFirst: boolean;
  onMostImportantFirstChange: (value: boolean) => void;
  shownCount: number;
  totalCount: number;
  /** Omitted when this list is not collecting a batch (the teaching-video list). */
  selection?: {
    selectableCount: number;
    selectedCount: number;
    onSelectAll: () => void;
    onClearSelection: () => void;
  };
  /** Keeps the two bars' input ids distinct when both render on one page. */
  idPrefix: string;
}) {
  // Nothing to triage: one item, one level. The controls would be furniture.
  if (totalCount < 2 && !selection) return null;

  const filterId = `${idPrefix}-importance-filter`;
  const sortId = `${idPrefix}-importance-sort`;

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2"
      data-testid={`${idPrefix}-triage-bar`}
    >
      <div className="flex items-center gap-2">
        <label htmlFor={filterId} className="text-xs text-text-muted">
          Show
        </label>
        <select
          id={filterId}
          value={importanceFilter}
          onChange={(e) => onImportanceFilterChange(e.target.value as ImportanceLevel | 'all')}
          className="rounded-md border border-border-subtle bg-dark-elevated-2 px-2 py-1
            text-xs text-text-primary"
        >
          <option value="all">All</option>
          {levelsPresent.map((level) => (
            <option key={level} value={level}>
              {IMPORTANCE_FILTER_LABELS[level]}
            </option>
          ))}
        </select>
      </div>

      <label htmlFor={sortId} className="flex items-center gap-1.5 text-xs text-text-muted">
        <input
          id={sortId}
          type="checkbox"
          checked={mostImportantFirst}
          onChange={(e) => onMostImportantFirstChange(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-border-subtle bg-dark-elevated-2"
        />
        Most important first
      </label>

      {importanceFilter !== 'all' && (
        <span className="text-xs text-text-muted">
          Showing {shownCount} of {totalCount}
        </span>
      )}

      {selection && selection.selectableCount > 0 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={selection.onSelectAll}
            className="rounded border border-accent px-2 py-1 text-xs font-medium text-accent
              transition-colors hover:bg-accent/10"
          >
            Select all ready to add
          </button>
          {selection.selectedCount > 0 && (
            <button
              type="button"
              onClick={selection.onClearSelection}
              className="rounded border border-border-subtle px-2 py-1 text-xs font-medium
                text-text-secondary transition-colors hover:bg-dark-elevated-1"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Can this check be measured on this demo? (PR-8)
// ---------------------------------------------------------------------------

/**
 * The authoring-time answer to "will this check actually measure anything on my
 * demo?" — a short label, and the backend's own what / why / how underneath it
 * when there is something to do about it.
 *
 * Why it exists: the coach's most canonical rule on their own diagonal-camera
 * template compiled cleanly, was accepted, and then measured nothing on every
 * evaluation (audit item `RC-4`); a height comparison on the same template had
 * no reference value to calibrate against (`RC-5`). Neither was discoverable
 * before accepting. The verdict is the backend's — this component decides
 * nothing (`utils/ruleCompilerDisplay.applicabilityLabel`).
 */
function ApplicabilityRow({ applicability }: { applicability: GoalApplicability }) {
  return (
    <div className="space-y-1.5" data-testid="applicability">
      {/* The label is a sentence, not a word ("We can't tell from this demo —
          set the camera angle when you save"), and a Badge is `inline-flex`
          with no wrapping of its own: at phone width it would run off the
          card. */}
      <Badge
        variant={applicabilityVariant(applicability)}
        size="sm"
        className="max-w-full whitespace-normal text-left"
      >
        {applicabilityLabel(applicability)}
      </Badge>
      {applicability.notice && (
        <ul className="list-none p-0 m-0">
          <NoticeRow notice={applicability.notice} />
        </ul>
      )}
    </div>
  );
}

// NO confidence badge, here or on the compiled-rule card below (audit item
// TV-11 / E2E-20, frontend half). The old badge printed the extractor's
// confidence as a percentage and coloured it below 0.5 — a number the coach
// could do nothing with, attached to nothing that changed. On the live
// 12-goal run every goal came back at 0.8, including one that was not
// measurable at all, so the badge did not even discriminate. When low-quality
// source wording actually matters, the backend says so in words, as a
// `low_confidence_source` notice with a what / why / how
// (docs/COACH_FACING_COPY_STANDARD.md §2: "numbers only when the coach can
// act on them").

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({
  text,
  onCopy,
  label,
}: {
  text: string;
  onCopy: (text: string) => void;
  label: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    onCopy(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={`Add "${text}" to template`}
      className="flex items-center gap-1.5 w-full text-left px-3 py-2 rounded
        hover:bg-dark-elevated-1 transition-colors group"
    >
      <span className="flex-1 text-sm text-text-primary">{text}</span>
      {copied ? (
        <CheckIcon className="h-4 w-4 text-success flex-shrink-0" />
      ) : (
        <ClipboardDocumentIcon className="h-4 w-4 text-text-muted group-hover:text-accent flex-shrink-0" />
      )}
      <span className="sr-only">{label}</span>
    </button>
  );
}

/** The Accept button's label for each card state. */
function acceptLabel(state: CardState): string {
  switch (state) {
    case 'pending':
      return 'Adding…';
    case 'added':
      return 'Added';
    case 'not_added':
      return 'Not added';
    default:
      return 'Accept';
  }
}

function AcceptDismissRow({
  state,
  disabled,
  confirmPrompt,
  onAccept,
  onDismiss,
}: {
  state: CardState;
  disabled: boolean;
  confirmPrompt: string;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const [pendingDismiss, setPendingDismiss] = useState(false);

  if (pendingDismiss) {
    /* Inline confirm — matches ChannelManagePage confirm-popover pattern */
    return (
      <div className="flex items-center gap-2 pt-1 px-2 py-1.5 rounded border
        border-border-subtle bg-dark-elevated-1">
        <span className="flex-1 text-xs text-text-secondary">{confirmPrompt}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="px-2.5 py-1 rounded text-xs font-semibold
            bg-danger text-white hover:bg-danger/80 transition-colors"
        >
          Dismiss
        </button>
        <button
          type="button"
          onClick={() => setPendingDismiss(false)}
          className="px-2.5 py-1 rounded text-xs font-medium
            border border-border-subtle text-text-muted hover:bg-dark-elevated-2
            transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 pt-1">
      <button
        type="button"
        onClick={onAccept}
        disabled={disabled || state === 'pending' || state === 'added'}
        className="flex-1 min-w-[7rem] px-3 py-1.5 rounded text-xs font-medium
          bg-accent text-dark-base hover:bg-accent-hover transition-colors
          disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {acceptLabel(state)}
      </button>
      <button
        type="button"
        onClick={() => setPendingDismiss(true)}
        className="px-3 py-1.5 rounded text-xs font-medium
          border border-border-subtle text-text-secondary hover:bg-dark-elevated-1
          transition-colors"
      >
        Dismiss
      </button>
    </div>
  );
}

/**
 * The two numbers behind one added check, and the one action on them (PR-7,
 * plan §C.3b(3)).
 *
 * Renders, in the order a coach reads:
 *   1. the CONFLICT note — the backend's own what / why / how, VERBATIM
 *      through the ordinary notice renderer. Only when the backend called it a
 *      conflict AND there is a measured number to compare against ("the badge
 *      renders only when a measured_value exists").
 *   2. which number this check will be judged against, with the number in it.
 *   3. the quiet agreement line, when the two numbers match.
 *   4. the one action, naming the number the coach would switch TO.
 *
 * Every string comes from `utils/verbalTargetCopy.ts`; this component composes
 * none and decides nothing — `conflict` and `priority` are the backend's.
 */
function VerbalTargetRow({
  verbalTarget,
  conflictNotice,
  pending,
  disabled,
  onSwitch,
}: {
  verbalTarget: VerbalTarget;
  /** The `verbal_target_conflict` notice for this check, when the server sent one. */
  conflictNotice?: Notice;
  /** True while this check is being rebuilt against the other number. */
  pending: boolean;
  disabled: boolean;
  onSwitch?: () => void;
}) {
  const conflict = hasVerbalTargetConflict(verbalTarget);
  const judged = judgedAgainstLine(verbalTarget);
  // No switch offered without a handler: the action's whole point is that the
  // check the coach already has is REPLACED, and only the panel can do that.
  const switchLabel =
    onSwitch && offersVerbalTargetSwitch(verbalTarget)
      ? switchActionLabel(verbalTarget)
      : null;

  if (!judged && !conflict && !switchLabel) return null;

  return (
    <div className="space-y-1.5" data-testid="verbal-target">
      {conflict && conflictNotice && (
        <ul className="list-none p-0 m-0">
          <NoticeRow notice={conflictNotice} />
        </ul>
      )}
      {judged && (
        <p
          className="text-xs font-medium text-text-secondary break-words"
          data-testid="verbal-target-judged"
        >
          {judged}
        </p>
      )}
      {showsMatchesDemo(verbalTarget) && (
        <p className="text-xs text-text-muted">{MATCHES_YOUR_DEMO_SENTENCE}</p>
      )}
      {switchLabel && (
        <button
          type="button"
          onClick={onSwitch}
          disabled={pending || disabled}
          className="w-full px-3 py-1.5 rounded text-xs font-medium
            border border-accent text-accent hover:bg-accent/10 transition-colors
            disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending ? SWITCH_PENDING_LABEL : switchLabel}
        </button>
      )}
    </div>
  );
}

function GoalCard({
  goal,
  state,
  notice,
  verbalTarget,
  verbalTargetNotice,
  onSwitchVerbalTarget,
  applicability,
  disabled,
  onAccept,
  onDismiss,
}: {
  goal: SuggestedGoal;
  state: CardState;
  /** The backend's explanation when this item was NOT turned into a check. */
  notice?: Notice;
  /** PR-7 — present once this suggestion became a check that states a number. */
  verbalTarget?: VerbalTarget;
  verbalTargetNotice?: Notice;
  onSwitchVerbalTarget?: () => void;
  /**
   * PR-8 — whether the check the server built can be measured on this demo.
   * Present once this suggestion HAS been materialised: on this path the
   * verdict is attached to the annotation the materialiser returned, so there
   * is nothing to show before then.
   */
  applicability?: GoalApplicability;
  disabled: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3 space-y-2">
      <div className="flex items-start gap-2 flex-wrap">
        <Badge variant="secondary" size="sm" className="flex-shrink-0 mt-0.5">
          {GOAL_TYPE_LABELS[goal.goal_type] ?? GENERIC_GOAL_TYPE_LABEL}
        </Badge>
        <Badge
          variant={IMPORTANCE_VARIANTS[goal.importance] ?? 'default'}
          size="sm"
          className="flex-shrink-0 mt-0.5"
        >
          {goal.importance}
        </Badge>
      </div>

      <p className="text-sm text-text-primary">{goal.description}</p>

      {(() => {
        const bodyParts = landmarkDisplayNames(goal.body_parts);
        return bodyParts.length > 0 ? (
          <p className="text-xs text-text-muted">
            Body parts: {bodyParts.join(', ')}
          </p>
        ) : null;
      })()}

      {goal.target_phase && (
        <p className="text-xs text-text-muted">
          Phase hint: <span className="text-text-secondary">{goal.target_phase}</span>
        </p>
      )}

      {goal.coach_quote && (
        <blockquote className="text-xs italic text-text-muted border-l-2 border-border-subtle pl-2">
          "{goal.coach_quote}"
        </blockquote>
      )}

      {/* The reason this one was not added, on the card itself — the coach
          should not have to scroll to a list to find out (E2E-5 / E2E-6). */}
      {notice && (
        <ul className="list-none p-0 m-0">
          <NoticeRow notice={notice} />
        </ul>
      )}

      {/* PR-8 — can the check we just built actually be measured on this demo? */}
      {applicability && <ApplicabilityRow applicability={applicability} />}

      {/* PR-7 — what you said vs what your body did, once this is a check. */}
      {verbalTarget && (
        <VerbalTargetRow
          verbalTarget={verbalTarget}
          conflictNotice={verbalTargetNotice}
          pending={state === 'pending'}
          disabled={disabled}
          onSwitch={onSwitchVerbalTarget}
        />
      )}

      <AcceptDismissRow
        state={state}
        disabled={disabled}
        confirmPrompt="Remove this suggestion?"
        onAccept={onAccept}
        onDismiss={onDismiss}
      />
    </div>
  );
}

function EmphasisCard({
  emphasis,
  state,
  notice,
  disabled,
  onAccept,
  onDismiss,
}: {
  emphasis: BodyPartEmphasis;
  state: CardState;
  notice?: Notice;
  disabled: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3 space-y-2">
      <div className="flex items-start gap-2 flex-wrap">
        <Badge variant="secondary" size="sm" className="flex-shrink-0 mt-0.5">
          {landmarkDisplayName(emphasis.landmark) ?? 'body part'}
        </Badge>
        <Badge
          variant={IMPORTANCE_VARIANTS[emphasis.emphasis] ?? 'default'}
          size="sm"
          className="flex-shrink-0 mt-0.5"
        >
          {emphasis.emphasis}
        </Badge>
        <Badge variant="default" size="sm" className="flex-shrink-0 mt-0.5">
          {emphasis.phase_name ? emphasis.phase_name : 'whole video'}
        </Badge>
      </div>

      {emphasis.source_quote && (
        <blockquote className="text-xs italic text-text-muted border-l-2 border-border-subtle pl-2">
          "{emphasis.source_quote}"
        </blockquote>
      )}

      {notice && (
        <ul className="list-none p-0 m-0">
          <NoticeRow notice={notice} />
        </ul>
      )}

      <AcceptDismissRow
        state={state}
        disabled={disabled}
        confirmPrompt="Remove this area to watch?"
        onAccept={onAccept}
        onDismiss={onDismiss}
      />
    </div>
  );
}

/**
 * One phase the coach described: its NAME, whether one of their own phases
 * matches that name, its cues, and the hints the extractor heard.
 *
 * NO frames, no proposed boundary, no guess at one — owner decision D8
 * (2026-10-07): "Coaches will still need to define phase boundaries via the UI
 * (not via voice)." The plan's earlier draft had this card seeding start/end
 * frames from a splitter; D8 dropped that, the segmentation flag and metric
 * M10 with it. What remains is the half that was always the real problem: a
 * phase NAME that matches nothing scopes its checks to the whole movement, and
 * on the live 30-suggestion run that happened to 28 of 30 without a word to
 * the coach (audit items `TV-12` / `FE-13` / `E2E-2`).
 *
 * The match is computed with `normalizePhaseName` — the SAME normalisation the
 * backend materialiser applies — so this card cannot promise a match the server
 * will not make.
 */
function PhaseCard({
  phase,
  templatePhases,
}: {
  phase: CoachingPhase;
  /** The coach's own phases. Omitted before they have defined any. */
  templatePhases?: Phase[];
}) {
  const matched = (templatePhases ?? []).find(
    (p) => normalizePhaseName(p.name) === normalizePhaseName(phase.name),
  );
  const hasTemplatePhases = (templatePhases?.length ?? 0) > 0;

  return (
    <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-medium text-text-primary">{phase.name}</p>
        {phase.approximate_duration_hint && (
          <Badge variant="default" size="sm">~{phase.approximate_duration_hint}</Badge>
        )}
        {hasTemplatePhases && (
          <Badge
            variant={matched ? 'success' : 'warning'}
            size="sm"
            // The coach's own phase name goes inside this label and can be
            // long, so it wraps rather than running off the card.
            className="max-w-full whitespace-normal text-left"
          >
            {matched ? `Matches your ‘${matched.name}’ phase` : 'No phase of yours matches'}
          </Badge>
        )}
      </div>
      {hasTemplatePhases && !matched && (
        <p className="text-xs text-text-muted">
          Checks you said belong to this phase will be checked across the whole movement
          instead. Rename one of your phases to this name — &lsquo;Align coaching
          phases&rsquo; above does it in one click.
        </p>
      )}
      {phase.description && (
        <p className="text-xs text-text-secondary">{phase.description}</p>
      )}
      {phase.cues.length > 0 && (
        <div>
          <p className="text-xs font-medium text-text-muted mb-1">Cues:</p>
          <ul className="space-y-0.5">
            {phase.cues.map((cue, i) => (
              <li key={i} className="text-xs text-text-secondary pl-2">
                &bull; {cue}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(() => {
        const bodyParts = landmarkDisplayNames(phase.body_parts_mentioned);
        return bodyParts.length > 0 ? (
          <p className="text-xs text-text-muted">
            Body parts: {bodyParts.join(', ')}
          </p>
        ) : null;
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rule-compiler suggestion card (contract §6)
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<ValidatedSuggestion['status'], string> = {
  validated: 'Ready to add',
  failed_validation: 'Could not validate',
  not_expressible: 'Not yet supported',
  // PR-8's three. `declaration_required` is NOT a failure: nothing about the
  // rule is wrong, we refused to guess which side leads rather than scoring
  // the wrong leg (LFT-103's "ask, don't guess").
  declaration_required: 'Needs the leading side',
  dismissed: 'Dismissed',
  accepted: 'Already added',
};

const STATUS_VARIANTS: Record<ValidatedSuggestion['status'], 'success' | 'warning' | 'default'> = {
  validated: 'success',
  failed_validation: 'warning',
  not_expressible: 'default',
  declaration_required: 'warning',
  dismissed: 'default',
  accepted: 'success',
};

/**
 * Statuses whose only coach-facing content is a refusal sentence, and which are
 * therefore NOT rendered as cards here (audit item `FE-12`, plan `RC-13`).
 *
 * The same refusal used to render twice on one page in two different wordings:
 * once as this card's `rationale`, and once in the durable
 * `RefusedRuleNotes` list on `TemplateDetails` fed by
 * `metadata.refused_rule_details` / `refused_rule_texts`. The durable list
 * wins — it survives a reload and a recompile, and it is the key the backend
 * persists. This list says which statuses it owns.
 */
const REFUSAL_ONLY_STATUSES: ReadonlySet<ValidatedSuggestion['status']> = new Set([
  'not_expressible',
]);

/**
 * The notice kinds that are about the HARD LIMIT, and therefore say the same
 * thing as the composed `gate_summary` sentence.
 *
 * PR-5's own rule (`utils/gateCopy.ts`): "where the backend ships a whole
 * notice, its what / why / how is rendered VERBATIM and this module composes
 * nothing." So when one of these arrives it WINS and the composed sentence is
 * suppressed — one message about one thing, which is the whole point of
 * `FE-12`. Everything else in `notices` renders alongside.
 */
const GATE_NOTICE_KINDS: ReadonlySet<string> = new Set([
  'gate_needs_a_number',
  'gate_not_possible_for_this_check',
  'gate_threshold_source',
]);

/**
 * What kind of instruction this is, in coach words (owner decision D3: "yes,
 * positive instructions should become checks").
 *
 * The badge used to read "praise" / "fault". Both are the system's words for
 * what it decided about the coach, and "praise" framed a positive instruction
 * as a compliment rather than as a check the coach can add — which is exactly
 * what D3 says it must not be. A coach says "something to do" and "something
 * to avoid", and the two read as the same KIND of thing: an instruction, which
 * either way becomes a check they accept.
 */
const SENTIMENT_BADGE_LABELS: Record<'positive' | 'negative', string> = {
  positive: 'Something to do',
  negative: 'Something to avoid',
};

/** What clicking the badge does, said as an action rather than a correction. */
const SENTIMENT_FLIP_TITLE =
  'We worked this out from your own words — click to switch it';

/**
 * Coach-override sentiment badge + flip control (backlog/critique-polarity-
 * reward-penalty-epic.md, PR1 "Coach-override surface"). What the compiler read
 * out of the coach's wording is shown as a badge; clicking it flips the LOCAL
 * (pre-accept) value between 'positive'/'negative'. Nothing is persisted
 * until Accept — this mirrors the owner's Decision 1 ("coach declaration is
 * authoritative"): the classifier's guess is a starting point, not a final
 * answer. Renders nothing when there is no classified value to show (a
 * legacy suggestion predating this field — see
 * `CompileRulesStatusResponse.sentiment_missing` for the recompile signal).
 *
 * Both values are ACCEPTABLE suggestions: a positive instruction goes through
 * the same Accept button, with the same wording around it, as a negative one
 * (D3). Nothing on this card implies a positive one is a critique.
 */
function SentimentBadge({
  value,
  onFlip,
}: {
  value: 'positive' | 'negative';
  onFlip: () => void;
}) {
  const isPositive = value === 'positive';
  return (
    <button
      type="button"
      onClick={onFlip}
      title={SENTIMENT_FLIP_TITLE}
      className="flex-shrink-0 mt-0.5"
    >
      <Badge
        variant={isPositive ? 'success' : 'warning'}
        size="sm"
        className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
      >
        {SENTIMENT_BADGE_LABELS[value]}
        <ArrowsRightLeftIcon className="h-3 w-3" />
      </Badge>
    </button>
  );
}

function CompiledRuleSuggestionCard({
  suggestion,
  onAccept,
  onDismiss,
  acceptDisabled = false,
  selected,
  onToggleSelected,
  onSentimentOverride,
}: {
  suggestion: ValidatedSuggestion;
  onAccept: (suggestion: ValidatedSuggestion) => boolean | void | Promise<boolean | void>;
  /**
   * Dismiss the suggestion. PR-8: this now PERSISTS (the server remembers the
   * id), so it can fail — a rejected promise is shown on the card rather than
   * the card disappearing and coming back on the next reload.
   */
  onDismiss: (suggestionId: string) => void | Promise<void>;
  /** True while ANY suggestion in the panel is being accepted (not just this card). */
  acceptDisabled?: boolean;
  /** Batch selection. Undefined when the parent is not collecting a batch. */
  selected?: boolean;
  onToggleSelected?: (suggestionId: string) => void;
  /**
   * Tell the parent the coach corrected this suggestion's badge, so the
   * correction can ride on the accept request's `overrides` — including when
   * this card is accepted as part of a batch and its own Accept is never
   * clicked.
   */
  onSentimentOverride?: (suggestionId: string, value: 'positive' | 'negative') => void;
}) {
  const [pendingDismiss, setPendingDismiss] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  // Set when the accept was DIVERTED (rule-channel accept-time hook excluded
  // it as a duplicate) rather than appended as a new scoring annotation —
  // see AnnotationSuggestionPanelProps.onAcceptCompiledRule's docstring.
  const [wasDiverted, setWasDiverted] = useState(false);
  // Local, pre-accept override — starts at the auto-classified value (or
  // null when the suggestion predates critique_sentiment). Flipping this
  // never calls the backend; it only changes what gets written onto the
  // annotation at Accept time (see handleAccept below).
  const [sentimentOverride, setSentimentOverride] = useState<'positive' | 'negative' | null>(
    suggestion.critique_sentiment ?? null,
  );

  const isValidated = suggestion.status === 'validated';
  const evidence = buildValidationEvidence(suggestion);
  // PR-5 — what this check will do when the athlete is off, from the backend's
  // own verdict on the annotation it built. `annotation` is deliberately
  // untyped (contract §2: appended VERBATIM, never re-typed field by field), so
  // `readGateSummary` is the single place that looks inside it and it fails
  // closed: an unrecognised shape renders nothing rather than a guess.
  const gateSummary = readGateSummary(suggestion.annotation?.gate_summary);
  const composedGateSentence = gateSummary ? gateSummarySentence(gateSummary) : null;
  // The backend's own notices about this suggestion, rendered verbatim.
  const suggestionNotices = suggestion.notices ?? [];
  const hasGateNotice = suggestionNotices.some((n) => GATE_NOTICE_KINDS.has(n.kind));
  const gateSentence = hasGateNotice ? null : composedGateSentence;

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    setDismissError(null);
    try {
      await onDismiss(suggestion.suggestion_id);
    } catch (err) {
      // A dismissal that did not reach the server must not look like one that
      // did — it would come back on the next reload and the coach would think
      // the dismissal was ignored (vision gap 7).
      setDismissError(
        err instanceof Error
          ? err.message
          : "We couldn't save that dismissal. Try again, or reload the page.",
      );
      setPendingDismiss(false);
    } finally {
      setDismissing(false);
    }
  };

  const handleAccept = async () => {
    // Guard against double-submit: bail if a request is already in flight
    // (this card OR another card in the panel — see acceptDisabled) or
    // already succeeded.
    if (accepting || accepted || acceptDisabled) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      // Coach-confirmed sentiment is stamped onto BOTH the suggestion field
      // and the annotation dict, and handed to the parent's accept handler.
      //
      // WHAT CARRIES IT TO THE SERVER is the accept request's `overrides` map,
      // not this dict. Until PR-8 the parent forwarded the dict itself into a
      // whole-template PUT (`compiledRuleAnnotationPassthrough`), which is how
      // a flip reached the saved annotation; an id-only accept has the server
      // build the annotation from its own stored value, so the correction
      // needed a channel of its own (`CompileRulesAcceptRequest.overrides`,
      // added by the orchestrator's ruling after this gap was reported). The
      // stamped dict is kept because it is what the parent reads to tell a real
      // correction from a flip-and-flip-back.
      const suggestionToAccept: ValidatedSuggestion =
        sentimentOverride && suggestion.annotation
          ? {
              ...suggestion,
              critique_sentiment: sentimentOverride,
              annotation: { ...suggestion.annotation, critique_sentiment: sentimentOverride },
            }
          : suggestion;
      const diverted = await onAccept(suggestionToAccept);
      setAccepted(true);
      if (diverted === true) setWasDiverted(true);
    } catch (err) {
      setAcceptError(
        err instanceof Error ? err.message : 'Could not add this rule. Please try again.',
      );
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3 space-y-2">
      <div className="flex items-start gap-2 flex-wrap">
        {/* Batch selection (vision gap 5: there was no "review all" mode —
            every card owned its own accept and the coach committed one at a
            time). Only a suggestion that is ready to add can be collected. */}
        {onToggleSelected && isValidated && (
          <label className="flex items-center gap-1.5 mt-0.5 flex-shrink-0 text-xs text-text-muted">
            <input
              type="checkbox"
              checked={selected ?? false}
              disabled={accepted || acceptDisabled}
              onChange={() => onToggleSelected(suggestion.suggestion_id)}
              className="h-3.5 w-3.5 rounded border-border-subtle bg-dark-elevated-2"
            />
            <span className="sr-only">
              Add &ldquo;{humanizeRelation(suggestion.relation)}&rdquo; in this batch
            </span>
          </label>
        )}
        <Badge variant="secondary" size="sm" className="flex-shrink-0 mt-0.5">
          {humanizeRelation(suggestion.relation)}
        </Badge>
        <Badge variant={STATUS_VARIANTS[suggestion.status]} size="sm" className="flex-shrink-0 mt-0.5">
          {STATUS_LABELS[suggestion.status]}
        </Badge>
        {sentimentOverride && (
          <SentimentBadge
            value={sentimentOverride}
            onFlip={() =>
              setSentimentOverride((prev) => {
                const next = prev === 'positive' ? 'negative' : 'positive';
                // Reported on EVERY flip, including a flip back to where it
                // started — the parent compares against the suggestion's own
                // classified value to decide what is actually a correction, so
                // this callback never has to remember anything.
                onSentimentOverride?.(suggestion.suggestion_id, next);
                return next;
              })
            }
          />
        )}
        {/* No `compiler_confidence` badge — see the note above GOAL_TYPE_LABELS. */}
      </div>

      {/*
        suggestion.rationale carries the full, specific explanation for
        EVERY status, including not_expressible -- the backend guarantees a
        coach-safe, relation-specific refusal sentence there (see
        rule_interpreter.py's RuleRelationSpec.coach_facing_refusal /
        _STRUCTURAL_REFUSAL_* constants, backlog/bugs/rule-compiler-refusal-
        text-leaks-internal-docs.md). A generic "can't be turned into a
        measurable check yet" line used to duplicate this AND overstate what
        was missing (Defect 2 in that ticket: the underlying measurement
        often already exists -- e.g. knee-past-toe -- only a scoreable goal
        type is missing) -- removed in favor of this single source of truth.
      */}
      <p className="text-sm text-text-primary">{suggestion.rationale}</p>

      {suggestion.coach_quote && (
        <blockquote className="text-xs italic text-text-muted border-l-2 border-border-subtle pl-2">
          &ldquo;{suggestion.coach_quote}&rdquo;
        </blockquote>
      )}

      {/*
        PR-5 — one line saying whether this check can fail an attempt, and at
        what angle. It sits above the actions because it changes what accepting
        this suggestion MEANS: a hard limit can cost an athlete the whole
        attempt, and a coach should not discover that after the fact.
      */}
      {gateSentence && (
        <p
          className="text-xs font-medium text-warning"
          data-testid="compiled-rule-gate-summary"
        >
          {gateSentence}
        </p>
      )}

      {/*
        PR-8 — whether this can be measured on the coach's own demo, BEFORE
        they accept it (audit items RC-4 / RC-5 / TV-5b).
      */}
      {suggestion.applicability && (
        <ApplicabilityRow applicability={suggestion.applicability} />
      )}

      {/*
        The backend's own messages about this suggestion, verbatim. Shipped on
        the wire since PR-5 and declared by nothing on this side, so a coach
        never read the one that matters most: only part of what you said became
        a check — confirm it measures what you meant (RC-19).
      */}
      {suggestionNotices.length > 0 && (
        <ul className="list-none p-0 m-0 space-y-1.5" data-testid="suggestion-notices">
          {suggestionNotices.map((suggestionNotice, i) => (
            <NoticeRow
              key={`${suggestionNotice.kind}-${i}`}
              notice={suggestionNotice}
            />
          ))}
        </ul>
      )}

      {evidence && (
        <p className="text-xs text-text-muted" data-testid="validation-evidence">
          {evidence.sentence}
        </p>
      )}

      {/*
        FE-7: "it didn't hold up" is only true when something was MEASURED.
        A check nobody could test — the clip it came from is gone, there is no
        correct clip to compare against, the body position could not be read —
        used to get this same sentence, which is the opposite of what happened.
        When `evidence.measured` is false the sentence above already explains
        it, in the reason's own words.
      */}
      {suggestion.status === 'failed_validation' && (evidence === null || evidence.measured) && (
        <p className="text-xs text-text-muted">
          This check didn&apos;t hold up against your calibration takes, so it wasn&apos;t added.
        </p>
      )}

      {acceptError && <p className="text-xs text-danger">{acceptError}</p>}
      {dismissError && <p className="text-xs text-danger">{dismissError}</p>}

      {/*
        FE-12 — the EXPLANATION for a refused accept lives in exactly one place,
        the parent's refusal row, which renders the backend's own what / why /
        how for it. This card used to carry its own sentence as well, so a
        duplicate accept produced two sentences about one event in two
        wordings. What stays here is the button LABEL below ("Recorded" rather
        than "Added"), which is this card's state and not a second copy of the
        reason — the signal that matters for arming
        SPORTFORM_RULE_CHANNEL_MINTING (docs/RUNBOOK_RULE_CHANNEL_ARMING.md's
        UX precondition) is that the coach is never shown a silent "looks like
        it worked", and the label plus the parent's row both say so.
      */}

      {/*
        Quiet provenance (deliverable 3, rule-compiler-refusal-text-leaks-
        internal-docs.md): which LLM produced this suggestion. Small print
        below the actionable content -- traceability, not a headline.
      */}
      {providerLabel(suggestion.compiler_provider) && (
        <p className="text-[10px] text-text-muted/60">
          Suggested using {providerLabel(suggestion.compiler_provider)}
        </p>
      )}

      {!pendingDismiss ? (
        <div className="flex gap-2 pt-1">
          {isValidated && (
            <button
              type="button"
              onClick={handleAccept}
              disabled={accepting || accepted || acceptDisabled}
              className="flex-1 px-3 py-1.5 rounded text-xs font-medium
                bg-accent text-dark-base hover:bg-accent-hover transition-colors
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {accepted ? (wasDiverted ? 'Recorded' : 'Added') : accepting ? 'Adding…' : 'Accept'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setPendingDismiss(true)}
            className="px-3 py-1.5 rounded text-xs font-medium
              border border-border-subtle text-text-secondary hover:bg-dark-elevated-1
              transition-colors"
          >
            Dismiss
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-1 px-2 py-1.5 rounded border
          border-border-subtle bg-dark-elevated-1">
          <span className="flex-1 text-xs text-text-secondary">
            Dismiss this suggestion? It won&apos;t come back.
          </span>
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={dismissing}
            className="px-2.5 py-1 rounded text-xs font-semibold
              bg-danger text-white hover:bg-danger/80 transition-colors
              disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {dismissing ? 'Dismissing…' : 'Dismiss'}
          </button>
          <button
            type="button"
            onClick={() => setPendingDismiss(false)}
            className="px-2.5 py-1 rounded text-xs font-medium
              border border-border-subtle text-text-muted hover:bg-dark-elevated-2
              transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Batch-selection wiring for the compiled-rule list. Owned by the PARENT
 * (`RuleCompilerPanel`), because the parent is what posts the batch to
 * `compile-rules/accept` and shows the dry-run preview — this list only
 * reports which cards the coach ticked.
 */
export interface CompiledRuleSelection {
  selectedIds: ReadonlySet<string>;
  onToggle: (suggestionId: string) => void;
  /** Called with the ids currently SHOWN and ready to add — never the hidden ones. */
  onSelectAll: (suggestionIds: string[]) => void;
  onClearSelection: () => void;
}

function CompiledRuleSuggestionsView({
  suggestions,
  onAccept,
  onDismiss,
  acceptDisabled,
  selection,
  onSentimentOverride,
}: {
  suggestions: ValidatedSuggestion[];
  onAccept: (suggestion: ValidatedSuggestion) => boolean | void | Promise<boolean | void>;
  onDismiss: (suggestionId: string) => void | Promise<void>;
  acceptDisabled?: boolean;
  selection?: CompiledRuleSelection;
  onSentimentOverride?: (suggestionId: string, value: 'positive' | 'negative') => void;
}) {
  const [importanceFilter, setImportanceFilter] = useState<ImportanceLevel | 'all'>('all');
  // OFF by default — see the note on `goalsMostImportantFirst` below: the list
  // arrives in the order the coach's own words produced it, and silently
  // renumbering that is not ours to do. The control is one click.
  const [mostImportantFirst, setMostImportantFirst] = useState(false);

  // FE-12 — a refusal has ONE surface, the durable list on the template page.
  // These are counted so the coach is still told they exist, and pointed at it.
  const refusedCount = suggestions.filter((s) => REFUSAL_ONLY_STATUSES.has(s.status)).length;
  const reviewable = suggestions.filter((s) => !REFUSAL_ONLY_STATUSES.has(s.status));

  const levelsPresent = IMPORTANCE_ORDER.filter((level) =>
    reviewable.some((s) => suggestionImportance(s) === level),
  );

  const filtered = reviewable.filter(
    (s) => importanceFilter === 'all' || suggestionImportance(s) === importanceFilter,
  );
  const shown = mostImportantFirst
    ? [...filtered].sort(
        (a, b) => importanceRank(suggestionImportance(a)) - importanceRank(suggestionImportance(b)),
      )
    : filtered;

  const selectableIds = shown
    .filter((s) => s.status === 'validated')
    .map((s) => s.suggestion_id);
  const selectedCount = selection
    ? selectableIds.filter((id) => selection.selectedIds.has(id)).length
    : 0;

  if (suggestions.length === 0) {
    return (
      <p className="text-sm text-text-muted p-4">
        No compilable rules found.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <SuggestionTriageBar
        idPrefix="compiled-rule"
        levelsPresent={levelsPresent}
        importanceFilter={importanceFilter}
        onImportanceFilterChange={setImportanceFilter}
        mostImportantFirst={mostImportantFirst}
        onMostImportantFirstChange={setMostImportantFirst}
        shownCount={shown.length}
        totalCount={reviewable.length}
        selection={
          selection
            ? {
                selectableCount: selectableIds.length,
                selectedCount,
                onSelectAll: () => selection.onSelectAll(selectableIds),
                onClearSelection: selection.onClearSelection,
              }
            : undefined
        }
      />

      {/*
        FE-12 — the refused rules themselves are NOT re-rendered here. They
        live in one place on this page, with their reasons, and a coach reading
        the same sentence twice in two different wordings cannot tell whether
        it is one problem or two.
      */}
      {refusedCount > 0 && (
        <p className="text-xs text-text-muted" data-testid="refusals-elsewhere">
          {refusedCount === 1
            ? "One of your rules couldn't be turned into a check."
            : `${refusedCount} of your rules couldn't be turned into checks.`}{' '}
          We can&apos;t measure them from a video the way you described them. Read why
          under &lsquo;Rules we could not turn into checks&rsquo; higher up this page.
        </p>
      )}

      {shown.length === 0 ? (
        <p className="text-sm text-text-muted">
          No suggestions at that importance. Choose &lsquo;All&rsquo; to see the rest.
        </p>
      ) : (
        <div className="space-y-2">
          {shown.map((suggestion) => (
            <CompiledRuleSuggestionCard
              key={suggestion.suggestion_id}
              suggestion={suggestion}
              onAccept={onAccept}
              onDismiss={onDismiss}
              acceptDisabled={acceptDisabled}
              selected={selection?.selectedIds.has(suggestion.suggestion_id)}
              onToggleSelected={selection?.onToggle}
              onSentimentOverride={onSentimentOverride}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AnnotationSuggestionPanel({
  teachingVideoId,
  templatePhases,
  onAnnotationsMaterialized,
  onAddCue,
  onAddMistake,
  compiledRuleSuggestions,
  onAcceptCompiledRule,
  onDismissCompiledRule,
  compiledRuleAcceptDisabled,
  compiledRuleSelection,
  onCompiledRuleSentimentOverride,
}: AnnotationSuggestionPanelProps) {
  const { data, isLoading, isError } = useTeachingVideoResult(teachingVideoId);

  // Track dismissed goals / areas to watch (UI-only; reappears on reload)
  const [dismissedGoals, setDismissedGoals] = useState<Set<number>>(new Set());
  const [dismissedEmphasis, setDismissedEmphasis] = useState<Set<number>>(new Set());

  // ── Bulk triage of the suggested checks (PR-8, vision gaps 5 + 13) ───────
  const [goalImportanceFilter, setGoalImportanceFilter] = useState<ImportanceLevel | 'all'>('all');
  /**
   * Sorting is OFF by default, deliberately.
   *
   * The suggestions arrive in the order the coach's own instruction produced
   * them, which is the order they said things in — a coach scanning the list
   * against their memory of the recording is reading that order. Re-ordering it
   * for them by default would be the UI quietly deciding their narration was
   * the wrong sequence. One click turns it on when they want to triage by
   * weight instead.
   */
  const [goalsMostImportantFirst, setGoalsMostImportantFirst] = useState(false);

  // ── Materialisation state ────────────────────────────────────────────────
  // Notices PERSIST in panel state for the session: a coach who accepts one
  // check, reads the note, and then accepts another must not lose the first
  // note. Re-accepting the same item replaces that item's notices rather than
  // stacking a second copy (matched on source_ref).
  const [notices, setNotices] = useState<Notice[]>([]);
  const [addedRefs, setAddedRefs] = useState<Set<string>>(new Set());
  const [notAddedRefs, setNotAddedRefs] = useState<Set<string>>(new Set());
  const [pendingRefs, setPendingRefs] = useState<Set<string>>(new Set());
  const [materializeError, setMaterializeError] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  // PR-7 — the check the server built for each suggestion, so the card can
  // show the two numbers behind it and a switch can REPLACE it rather than add
  // a second copy. Populated only where the join is a fact and not a guess:
  // see `mapAnnotationsToRequestedRefs`.
  const [materializedByRef, setMaterializedByRef] = useState<
    Record<string, MaterializedAnnotation>
  >({});
  // PR-7 — which number the coach chose for a goal, by goal index. Absent
  // means the default: the number they said wins.
  const [verbalPriorityChoices, setVerbalPriorityChoices] = useState<Record<number, boolean>>({});

  const runMaterialize = useCallback(
    async (
      goalIndices: number[],
      emphasisIndices: number[],
      isBulk: boolean,
      /**
       * PR-7 — the choice the coach just made, which has not reached
       * `verbalPriorityChoices` yet (a `setState` during this same click).
       * Merged over the stored choices for this run only.
       */
      choiceOverride?: Record<number, boolean>,
    ) => {
      if (!teachingVideoId) return;
      if (goalIndices.length === 0 && emphasisIndices.length === 0) return;

      const refs = [
        ...goalIndices.map(goalSourceRef),
        ...emphasisIndices.map(emphasisSourceRef),
      ];
      // The checks this run REBUILDS rather than adds (PR-7). Empty on a first
      // accept; one id when the coach switched which number a check uses.
      const replacesIds = refs
        .map((ref) => materializedByRef[ref]?.id)
        .filter((id): id is string => typeof id === 'string');
      setMaterializeError(null);
      setPendingRefs((prev) => new Set([...prev, ...refs]));
      if (isBulk) setBulkPending(true);

      try {
        const result = await materializeSuggestions(teachingVideoId, {
          goal_indices: goalIndices.length > 0 ? goalIndices : undefined,
          emphasis_indices: emphasisIndices.length > 0 ? emphasisIndices : undefined,
          template_phases: templatePhases,
          verbal_target_priority: buildVerbalTargetPriority(goalIndices, {
            ...verbalPriorityChoices,
            ...choiceOverride,
          }),
        });

        // E2E-5 / E2E-6: ONLY what the server returned in `annotations[]` is
        // appended. An item in `dropped[]` is not added, and the coach is
        // told why (below). Nothing is inferred from the counts.
        const droppedRefs = new Set(result.dropped.map((d) => d.source_ref));
        if (result.annotations.length > 0) {
          // A rebuilt check REPLACES the one the parent already holds; a new
          // one is appended. Two copies of the same check, each judged against
          // a different number, would both count.
          if (replacesIds.length > 0) {
            onAnnotationsMaterialized?.(result.annotations, replacesIds);
          } else {
            onAnnotationsMaterialized?.(result.annotations);
          }
        }

        // Remember (or forget) which check came from which suggestion. Refs in
        // this run are cleared first, so a re-run that can no longer be joined
        // leaves no stale annotation behind to show numbers from.
        const joined = mapAnnotationsToRequestedRefs(refs, result.annotations);
        setMaterializedByRef((prev) => {
          const next = { ...prev };
          for (const ref of refs) delete next[ref];
          return { ...next, ...joined };
        });

        setAddedRefs((prev) => {
          const next = new Set(prev);
          for (const ref of refs) {
            if (droppedRefs.has(ref)) next.delete(ref);
            else next.add(ref);
          }
          return next;
        });
        setNotAddedRefs((prev) => {
          const next = new Set(prev);
          for (const ref of refs) {
            if (droppedRefs.has(ref)) next.add(ref);
            else next.delete(ref);
          }
          return next;
        });
        setNotices((prev) => [
          ...prev.filter((n) => !refs.includes(n.source_ref)),
          ...result.notices,
        ]);
      } catch (err) {
        // The service already guarantees a coach-facing sentence here
        // (`MATERIALIZE_FAILURE` or the server's own, never axios's).
        setMaterializeError(err instanceof Error ? err.message : MATERIALIZE_FAILURE);
      } finally {
        setPendingRefs((prev) => {
          const next = new Set(prev);
          for (const ref of refs) next.delete(ref);
          return next;
        });
        if (isBulk) setBulkPending(false);
      }
    },
    [
      teachingVideoId,
      templatePhases,
      onAnnotationsMaterialized,
      materializedByRef,
      verbalPriorityChoices,
    ],
  );

  /**
   * The coach switched which number this check is judged against (PR-7).
   *
   * Records the choice and re-materialises that ONE goal, so the server
   * rebuilds the check against the other number. The choice is derived from
   * the `priority` the backend reported, never from a local idea of which
   * number is in force.
   */
  const switchVerbalTarget = useCallback(
    (goalIndex: number, verbalTarget: VerbalTarget) => {
      const choice = nextVerbalTargetPriority(verbalTarget);
      setVerbalPriorityChoices((prev) => ({ ...prev, [goalIndex]: choice }));
      void runMaterialize([goalIndex], [], false, { [goalIndex]: choice });
    },
    [runMaterialize],
  );

  const cardState = useCallback(
    (ref: string): CardState => {
      if (pendingRefs.has(ref)) return 'pending';
      if (notAddedRefs.has(ref)) return 'not_added';
      if (addedRefs.has(ref)) return 'added';
      return 'idle';
    },
    [pendingRefs, notAddedRefs, addedRefs],
  );

  /** The "not added" notice for one card, if the server sent one. */
  const noticeFor = useCallback(
    (ref: string): Notice | undefined =>
      notices.find((n) => n.source_ref === ref && n.severity === 'dropped') ??
      notices.find((n) => n.source_ref === ref),
    [notices],
  );

  /**
   * The conflict note for one card (PR-7) — the ONE place this panel looks at
   * a notice's `kind`.
   *
   * A check can carry several notes at once (a phase that did not match, a
   * standard strictness), and the conflict row must show the note about the
   * two NUMBERS, so `source_ref` alone does not identify it.
   */
  const verbalTargetNoticeFor = useCallback(
    (ref: string): Notice | undefined =>
      notices.find((n) => n.source_ref === ref && n.kind === VERBAL_TARGET_CONFLICT_KIND),
    [notices],
  );

  // Rule-compiler mode: suggestions are passed directly, no internal fetch.
  // Checked BEFORE the teaching-video loading/error branches below so this
  // mode never depends on (or is blocked by) teaching-video state.
  if (compiledRuleSuggestions !== undefined) {
    return (
      <CompiledRuleSuggestionsView
        suggestions={compiledRuleSuggestions}
        onAccept={onAcceptCompiledRule ?? (() => {})}
        onDismiss={onDismissCompiledRule ?? (() => {})}
        acceptDisabled={compiledRuleAcceptDisabled}
        selection={compiledRuleSelection}
        onSentimentOverride={onCompiledRuleSentimentOverride}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4">
        <Spinner size="sm" />
        <p className="text-sm text-text-secondary">Loading coach instruction analysis...</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-danger p-4">
        Failed to load coaching suggestions. Please try reloading.
      </p>
    );
  }

  if (data.status === 'processing' || data.status === 'uploaded') {
    return (
      <div className="flex items-center gap-2 p-4">
        <Spinner size="sm" />
        <p className="text-sm text-text-secondary">Analysing coach instruction... (1-2 minutes)</p>
      </div>
    );
  }

  if (data.status === 'failed') {
    return (
      <div className="flex items-center gap-2 p-4">
        <ExclamationTriangleIcon className="h-5 w-5 text-warning flex-shrink-0" />
        <p className="text-sm text-warning">
          {data.error_message || 'Extraction failed. You can continue without coach instruction.'}
        </p>
      </div>
    );
  }

  if (!data.result) {
    return (
      <p className="text-sm text-text-muted p-4">
        No suggestions available yet.
      </p>
    );
  }

  const { analysis, transcript } = data.result;
  const goalList = analysis.suggested_goals;
  // body_part_emphasis may be absent on analyses created before B-2026-0030.
  const emphasisList = analysis.body_part_emphasis ?? [];
  const visibleGoalIndices = goalList
    .map((_, i) => i)
    .filter((i) => !dismissedGoals.has(i));
  // Triage operates on INDICES, never on reordered copies of the goals: every
  // join in this panel — source_ref, the materialised check, the notice for a
  // card — is keyed by a goal's position in the extractor's list, so a sort
  // that renumbered them would silently re-point all three.
  const goalImportanceLevels = IMPORTANCE_ORDER.filter((level) =>
    visibleGoalIndices.some((i) => goalList[i].importance === level),
  );
  const triagedGoalIndices = visibleGoalIndices.filter(
    (i) => goalImportanceFilter === 'all' || goalList[i].importance === goalImportanceFilter,
  );
  const shownGoalIndices = goalsMostImportantFirst
    ? [...triagedGoalIndices].sort(
        (a, b) => importanceRank(goalList[a].importance) - importanceRank(goalList[b].importance),
      )
    : triagedGoalIndices;
  const visibleEmphasisIndices = emphasisList
    .map((_, i) => i)
    .filter((i) => !dismissedEmphasis.has(i));

  // "Add all" only offers what is still actionable: not dismissed, not already
  // added, not in flight.
  const pendingGoalIndices = visibleGoalIndices.filter(
    (i) => cardState(goalSourceRef(i)) === 'idle' || cardState(goalSourceRef(i)) === 'not_added',
  );
  const pendingEmphasisIndices = visibleEmphasisIndices.filter(
    (i) =>
      cardState(emphasisSourceRef(i)) === 'idle' ||
      cardState(emphasisSourceRef(i)) === 'not_added',
  );
  const addAllCount = pendingGoalIndices.length + pendingEmphasisIndices.length;

  const hasContent =
    visibleGoalIndices.length > 0 ||
    visibleEmphasisIndices.length > 0 ||
    analysis.common_mistakes.length > 0 ||
    analysis.phases.length > 0 ||
    (transcript?.terminology?.length ?? 0) > 0;

  // Notices with no card of their own (a merged duplicate, a whole-run note)
  // still have to be read, so the list below shows every notice. Per-card
  // notices are repeated there on purpose: the list is the record of the run.
  const runNotices = notices;

  return (
    <div className="space-y-5">
      {/* What the instruction was about */}
      {analysis.movement_summary && (
        <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3">
          <p className="text-xs font-medium text-text-muted mb-1">What we heard</p>
          <p className="text-sm text-text-secondary">{analysis.movement_summary}</p>
        </div>
      )}

      {/* Things to know about this extraction (E2E-20's warnings channel).
          Backend-authored sentences, rendered verbatim. */}
      {(analysis.warnings?.length ?? 0) > 0 && (
        <MaterializationNotices notices={analysis.warnings ?? []} />
      )}

      {/* Recovery phase undescribed warning (heuristic) */}
      {analysis.phases.some((p) => /recovery/i.test(p.name)) &&
        analysis.phases.find((p) => /recovery/i.test(p.name))?.description === '' && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-warning/30 bg-warning/10">
            <ExclamationTriangleIcon className="h-4 w-4 text-warning flex-shrink-0" />
            <p className="text-xs text-warning">
              Coach mentioned &apos;recovery&apos; but did not describe it. Add cues for this phase manually.
            </p>
          </div>
        )}

      {/* `Alert` carries role="alert" itself. */}
      {materializeError && <Alert variant="error">{materializeError}</Alert>}

      {/* Suggested checks */}
      {visibleGoalIndices.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-text-primary">
            Suggested checks ({visibleGoalIndices.length})
          </p>
          {/* FE-3: the old line here claimed "Accept opens the goal config form
              pre-filled. You place the annotation and adjust tolerances before
              it is created." No form opened and nothing was pre-filled — the
              check was appended and the coach had to go looking. */}
          {/* FE-3 — accept now OPENS the check it just added, in the
              checkpoint list below, with its settings filled in. Before PR-8
              the copy promised a pre-filled form that never opened and the
              check was appended silently; the coach had to go and find it. */}
          <p className="text-xs text-text-muted">
            The numbers you said out loud become the numbers each check uses.
            Add a check and it opens below, filled in, so you can place it and
            set how strict it is.
          </p>
          <SuggestionTriageBar
            idPrefix="suggested-check"
            levelsPresent={goalImportanceLevels}
            importanceFilter={goalImportanceFilter}
            onImportanceFilterChange={setGoalImportanceFilter}
            mostImportantFirst={goalsMostImportantFirst}
            onMostImportantFirstChange={setGoalsMostImportantFirst}
            shownCount={shownGoalIndices.length}
            totalCount={visibleGoalIndices.length}
          />
          {addAllCount > 1 && (
            <button
              type="button"
              onClick={() =>
                void runMaterialize(pendingGoalIndices, pendingEmphasisIndices, true)
              }
              disabled={bulkPending}
              className="px-3 py-1.5 rounded text-xs font-medium
                border border-accent text-accent hover:bg-accent/10 transition-colors
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {bulkPending ? 'Adding…' : 'Add all suggested checks'}
            </button>
          )}
          {shownGoalIndices.length === 0 ? (
            <p className="text-sm text-text-muted">
              No suggested checks at that importance. Choose &lsquo;All&rsquo; to see the rest.
            </p>
          ) : (
            <div className="space-y-2">
              {shownGoalIndices.map((i) => {
                const goal = goalList[i];
                const ref = goalSourceRef(i);
                const materialized = materializedByRef[ref];
                const verbalTarget = materialized?.verbal_target;
                return (
                  <GoalCard
                    key={i}
                    goal={goal}
                    state={cardState(ref)}
                    notice={notAddedRefs.has(ref) ? noticeFor(ref) : undefined}
                    verbalTarget={verbalTarget}
                    verbalTargetNotice={verbalTargetNoticeFor(ref)}
                    onSwitchVerbalTarget={
                      verbalTarget ? () => switchVerbalTarget(i, verbalTarget) : undefined
                    }
                    applicability={materialized?.applicability}
                    disabled={bulkPending}
                    onAccept={() => void runMaterialize([i], [], false)}
                    onDismiss={() => setDismissedGoals((prev) => new Set([...prev, i]))}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Areas to watch (body-part emphasis → weighted region) */}
      {visibleEmphasisIndices.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-text-primary">
            Areas to watch ({visibleEmphasisIndices.length})
          </p>
          <p className="text-xs text-text-muted">
            Body parts you kept coming back to. A part you stressed should count
            for more than the rest of the body.
            Added to your checkpoint list below; open it there to adjust.
          </p>
          <div className="space-y-2">
            {emphasisList.map((emphasis, i) => {
              if (dismissedEmphasis.has(i)) return null;
              const ref = emphasisSourceRef(i);
              return (
                <EmphasisCard
                  key={`emphasis-${i}`}
                  emphasis={emphasis}
                  state={cardState(ref)}
                  notice={notAddedRefs.has(ref) ? noticeFor(ref) : undefined}
                  disabled={bulkPending}
                  onAccept={() => void runMaterialize([], [i], false)}
                  onDismiss={() => setDismissedEmphasis((prev) => new Set([...prev, i]))}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* The record of every materialise run in this session */}
      <MaterializationNotices notices={runNotices} />

      {/* Coaching cues (from phases) */}
      {analysis.phases.some((p) => p.cues.length > 0) && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-text-primary">Coaching Cues</p>
          {/* FE-14 — cues now feed the rule compiler as well as the narrative,
              so this says what adding one does. The asymmetry with mistakes
              below is stated rather than hidden (plan §C.5). */}
          <p className="text-xs text-text-muted">
            Click one to put it on your template. We also read your cues when we
            turn your words into checks.
          </p>
          <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 divide-y divide-border-subtle">
            {analysis.phases.flatMap((phase) =>
              phase.cues.map((cue, j) => (
                <CopyButton
                  key={`${phase.name}-${j}`}
                  text={cue}
                  onCopy={onAddCue ?? (() => {})}
                  label={`Add cue "${cue}" to template`}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Common mistakes */}
      {analysis.common_mistakes.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-text-primary">Common Mistakes</p>
          {/* E2E-11 — a mistake can never become an accepted check by design:
              activating one needs a clip of it happening, and nothing in the UI
              or the API said so. The zero-evidence activation stays refused
              (it is a safety invariant); what changes is that the coach is told
              before they type twelve of these expecting checks. */}
          <p className="text-xs text-text-muted">
            Click one to put it on your template. We tell athletes to avoid these,
            but we can&apos;t score one until you add a clip of it happening.
          </p>
          <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 divide-y divide-border-subtle">
            {analysis.common_mistakes.map((mistake: CommonMistake, i) => (
              <CopyButton
                key={i}
                text={mistake.description}
                onCopy={onAddMistake ?? (() => {})}
                label={`Add mistake "${mistake.description}" to template`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Phases the instruction described — NAMES and hints only (D8) */}
      {analysis.phases.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-text-primary">
            Phases your instruction described ({analysis.phases.length})
          </p>
          <p className="text-xs text-text-muted">
            Set this phase&apos;s start and end on the timeline.
          </p>
          <div className="space-y-2">
            {analysis.phases.map((phase: CoachingPhase, i) => (
              <PhaseCard key={i} phase={phase} templatePhases={templatePhases} />
            ))}
          </div>
        </div>
      )}

      {!hasContent && (
        <p className="text-sm text-text-muted">
          No coaching suggestions were extracted. This can happen with very short
          or low-audio-quality recordings.
        </p>
      )}
    </div>
  );
}
